import { useDispatch, useSelector } from 'react-redux';
import { AuthShell, AuthCard } from '@widgets/auth-shell';
import { Button } from '@shared/ui';
import { ALL_ROUTES } from '@app/router';
import { navigateToRoute } from 'redux_store';
import './NotFoundPage.css';

const NotFoundIcon = () => (
  <svg
    className="qhs-not-found__icon"
    width="80"
    height="80"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.4"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <path d="M14 2v6h6" />
    <line x1="9" y1="13" x2="15" y2="13" />
    <line x1="9" y1="17" x2="15" y2="17" />
  </svg>
);

/**
 * NotFoundPage — 404 surface.
 *
 * Renders in two modes:
 *   - Logged-in: lands inside the AppShell content area (sidebar +
 *     topbar still visible). The user already has the sidebar to
 *     navigate from, so we omit the CTA — just icon + message.
 *   - Logged-out (defensive): lands inside AuthShell so the surface
 *     stays branded for anyone who reaches /not-found without a
 *     session. ProtectedRoute already redirects unauthenticated
 *     traffic to /login in normal flow; this branch is the safety
 *     net for transitional auth states. Keeps the "Return to
 *     Dashboard" CTA so they have a way out.
 */
const NotFoundCard = ({ showCta }) => {
  const dispatch = useDispatch();
  const { titles, forms, btn } = useSelector(state => state.i18n);

  const goHome = () => {
    dispatch(navigateToRoute({ route: { path: ALL_ROUTES.root.path } }));
  };

  return (
    <div className="qhs-not-found__card">
      <div className="qhs-not-found__icon-wrap">
        <NotFoundIcon />
      </div>
      <h1 className="qhs-not-found__title">{titles.pageNotFound}</h1>
      <p className="qhs-not-found__subtitle">
        {titles.pageNotFoundSubtitle || forms.notFoundCardSubtitle}
      </p>
      {showCta && (
        <Button variant="primary" size="lg" onClick={goHome} className="qhs-not-found__cta">
          {btn.returnToDashboard}
        </Button>
      )}
    </div>
  );
};

const NotFoundPage = () => {
  const { isLogin, user, metadata } = useSelector(state => state.user);
  const { titles, forms } = useSelector(state => state.i18n);

  const fullyAuthed = isLogin === true && user !== null && metadata !== null;

  if (!fullyAuthed) {
    return (
      <AuthShell
        brandWelcome={titles.welcomeToQHSLab}
        brandWelcomeStrong={titles.qhsLabBrand}
        brandTagline={forms.brandTagline}
        brandAttribution={forms.brandAttribution}
      >
        <AuthCard>
          <NotFoundCard showCta />
        </AuthCard>
      </AuthShell>
    );
  }

  return (
    <div className="qhs-not-found">
      <NotFoundCard showCta={false} />
    </div>
  );
};

export default NotFoundPage;
